"use client";

import { Dialog as DialogPrimitive } from "@base-ui/react/dialog";
import { SearchIcon } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import type React from "react";
import { cn } from "@/lib/utils";

/**
 * Command — a ⌘K command palette built on Base UI's Dialog. Filters a flat list
 * of actions by label/keywords, groups them, and supports full keyboard
 * navigation (Arrow keys + Enter). Ported to our idiom from the Calm Authority
 * design system (docs/design-system/components/overlays/Command); the app has no
 * cmdk dependency, so navigation is implemented directly.
 *
 * The stateful body lives in an inner component rendered inside the Dialog
 * popup. Base UI unmounts the popup content when the palette closes, so the
 * inner component remounts fresh on every open — query and selection reset
 * without any effect. All user-facing text is passed in already translated.
 */

export interface CommandItem {
  id: string;
  label: string;
  group?: string;
  keywords?: string[];
  shortcut?: string;
  icon?: React.ComponentType<{ className?: string }>;
  onSelect: () => void;
}

export interface CommandProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  items: CommandItem[];
  placeholder?: string;
  emptyText?: string;
  label?: string;
  /** Live query text, called on every keystroke (undebounced) so a caller can drive a debounced remote lookup (e.g. audit search) that this component has no knowledge of. */
  onQueryChange?: (query: string) => void;
  /** Remote results appended as their own group -- bypassing the local label/keyword filter, since the caller has already matched them against the query (a match can come from a field, like a source URL, that never appears in `label`). */
  remoteItems?: CommandItem[];
  remoteLoading?: boolean;
  /** Only rendered while remoteLoading is true; omit to render nothing extra. */
  remoteLoadingText?: string;
}

interface CommandBodyProps {
  items: CommandItem[];
  placeholder?: string;
  emptyText?: string;
  onClose: () => void;
  inputRef: React.RefObject<HTMLInputElement | null>;
  onQueryChange?: (query: string) => void;
  remoteItems?: CommandItem[];
  remoteLoading?: boolean;
  remoteLoadingText?: string;
}

function CommandBody({
  items,
  placeholder,
  emptyText,
  onClose,
  inputRef,
  onQueryChange,
  remoteItems,
  remoteLoading,
  remoteLoadingText,
}: CommandBodyProps): React.ReactElement {
  const [query, setQuery] = useState("");
  const [highlighted, setHighlighted] = useState(0);
  const listRef = useRef<HTMLDivElement>(null);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    const staticMatches = q
      ? items.filter((item) => {
        const haystack = `${item.label} ${item.keywords?.join(" ") ?? ""}`
          .toLowerCase();
        return haystack.includes(q);
      })
      : items;
    // Remote results (e.g. audit search) are pre-matched by the caller
    // against the live query -- never re-filtered against label/keywords,
    // since a remote match can come from a field (source URL, industry
    // sector) that never appears in `label`.
    return remoteItems && remoteItems.length > 0
      ? [...staticMatches, ...remoteItems]
      : staticMatches;
  }, [items, query, remoteItems]);

  // Clamp the highlight into the current filtered range (derived, no effect).
  const activeIndex =
    filtered.length === 0 ? -1 : Math.min(highlighted, filtered.length - 1);

  // Scroll the highlighted row into view on keyboard navigation. Pure DOM
  // side-effect — no state is set here.
  useEffect(() => {
    const node = listRef.current?.querySelector<HTMLElement>(
      '[data-highlighted="true"]',
    );
    node?.scrollIntoView({ block: "nearest" });
  }, [activeIndex]);

  const select = (item: CommandItem): void => {
    onClose();
    item.onSelect();
  };

  const onKeyDown = (event: React.KeyboardEvent<HTMLInputElement>): void => {
    if (event.key === "ArrowDown") {
      event.preventDefault();
      setHighlighted(Math.min(activeIndex + 1, filtered.length - 1));
    } else if (event.key === "ArrowUp") {
      event.preventDefault();
      setHighlighted(Math.max(activeIndex - 1, 0));
    } else if (event.key === "Enter") {
      event.preventDefault();
      const item = filtered[activeIndex];
      if (item) {
        select(item);
      }
    }
  };

  // Group the filtered items, preserving first-seen group order, while tracking
  // each item's flat index (used for the shared highlight cursor).
  const groups: { name: string; items: { item: CommandItem; index: number }[] }[] =
    [];
  const byName = new Map<string, { item: CommandItem; index: number }[]>();
  filtered.forEach((item, index) => {
    const name = item.group ?? "";
    let bucket = byName.get(name);
    if (!bucket) {
      bucket = [];
      byName.set(name, bucket);
      groups.push({ name, items: bucket });
    }
    bucket.push({ item, index });
  });

  return (
    <>
      <div className="flex items-center gap-2.5 border-b px-3.5">
        <SearchIcon
          aria-hidden="true"
          className="size-4 shrink-0 text-[var(--ink-3)]"
        />
        <input
          className="h-12 w-full bg-transparent text-sm text-foreground outline-none placeholder:text-[var(--ink-3)]"
          data-test="command-input"
          onChange={(event) => {
            const value = event.target.value;
            setQuery(value);
            setHighlighted(0);
            onQueryChange?.(value);
          }}
          onKeyDown={onKeyDown}
          placeholder={placeholder}
          ref={inputRef}
          type="text"
          value={query}
        />
      </div>
      <div
        className="flex-1 overflow-y-auto p-1.5"
        data-slot="command-list"
        ref={listRef}
        role="listbox"
      >
        {filtered.length === 0 && !remoteLoading ? (
          <div
            className="px-3 py-6 text-center text-[var(--ink-3)] text-sm"
            data-test="command-empty"
          >
            {emptyText}
          </div>
        ) : (
          <>
            {groups.map((group) => (
              <div key={group.name || "_"} className="mb-1 last:mb-0">
                {group.name ? (
                  <div className="px-2.5 pt-2 pb-1 font-medium text-[var(--ink-3)] text-xs uppercase tracking-wide">
                    {group.name}
                  </div>
                ) : null}
                {group.items.map(({ item, index }) => {
                  const Icon = item.icon;
                  const isActive = index === activeIndex;
                  return (
                    <button
                      key={item.id}
                      className={cn(
                        "flex w-full items-center gap-2.5 rounded-sm px-2.5 py-2 text-left text-sm outline-none",
                        isActive
                          ? "bg-accent text-accent-foreground"
                          : "text-foreground",
                      )}
                      data-highlighted={isActive}
                      data-test="command-item"
                      onClick={() => select(item)}
                      onMouseMove={() => setHighlighted(index)}
                      role="option"
                      aria-selected={isActive}
                      type="button"
                    >
                      {Icon ? (
                        <Icon className="size-4 shrink-0 opacity-80" />
                      ) : null}
                      <span className="flex-1 truncate">{item.label}</span>
                      {item.shortcut ? (
                        <kbd className="font-[var(--mono)] text-[var(--ink-4)] text-xs">
                          {item.shortcut}
                        </kbd>
                      ) : null}
                    </button>
                  );
                })}
              </div>
            ))}
            {remoteLoading && remoteLoadingText ? (
              <div
                className="px-3 py-2 text-center text-[var(--ink-3)] text-xs"
                data-test="command-remote-loading"
              >
                {remoteLoadingText}
              </div>
            ) : null}
          </>
        )}
      </div>
    </>
  );
}

export function Command({
  open,
  onOpenChange,
  items,
  placeholder,
  emptyText,
  label,
  onQueryChange,
  remoteItems,
  remoteLoading,
  remoteLoadingText,
}: CommandProps): React.ReactElement {
  const inputRef = useRef<HTMLInputElement>(null);
  return (
    <DialogPrimitive.Root open={open} onOpenChange={onOpenChange}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Backdrop
          className="fixed inset-0 z-50 bg-black/32 backdrop-blur-sm transition-opacity duration-150 data-ending-style:opacity-0 data-starting-style:opacity-0"
          data-slot="command-backdrop"
        />
        <DialogPrimitive.Popup
          className="fixed top-[12vh] left-1/2 z-50 flex max-h-[70vh] w-[min(92vw,560px)] -translate-x-1/2 flex-col overflow-hidden rounded-xl border bg-popover not-dark:bg-clip-padding text-popover-foreground shadow-lg/5 transition-[transform,opacity] duration-150 data-ending-style:opacity-0 data-starting-style:opacity-0 data-ending-style:scale-98 data-starting-style:scale-98"
          data-slot="command-palette"
          data-test="command-palette"
          initialFocus={inputRef}
        >
          <DialogPrimitive.Title className="sr-only">
            {label ?? placeholder ?? "Command"}
          </DialogPrimitive.Title>
          <CommandBody
            emptyText={emptyText}
            inputRef={inputRef}
            items={items}
            onClose={() => onOpenChange(false)}
            onQueryChange={onQueryChange}
            placeholder={placeholder}
            remoteItems={remoteItems}
            remoteLoading={remoteLoading}
            remoteLoadingText={remoteLoadingText}
          />
        </DialogPrimitive.Popup>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}
